import { API_CONFIG } from "../config/environment";

const createAuthHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  if (response.status === 401 || response.status === 403) {
    console.warn("🔐 Token expirado o no autorizado en API");
    window.dispatchEvent(
      new CustomEvent("token-expired", {
        detail: { status: response.status },
      }),
    );

    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message || "Token expirado o no autorizado",
    );
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message || `HTTP error! status: ${response.status}`,
    );
  }

  return response.json();
};

const BASE = `${API_CONFIG.V2_BASE_URL}/bienes/direct-sales/lot-details`;

/**
 * Servicio para gestionar la SECCIÓN de cotización (quoteSection) de las líneas
 * de detalle de un lote de venta directa.
 *
 * Permite indicar qué características/datos del lote aparecen como
 * "Características" o "Documentación" en el PDF de cotización generado para
 * venta de usados. NO crea ni elimina líneas de detalle, solo asigna su sección.
 */
export const lotDetailsService = {
  /**
   * Obtener las líneas de detalle de un lote con su sección de cotización.
   *
   * @param {number|string} lotId
   * @param {string} token
   * @returns {Promise<Array<{id:number,label:string,value:string,typeCode:(string|null),quoteSection:(string|null)}>>}
   */
  getLotDetails: async (lotId, token) => {
    if (!token) {
      throw new Error("Token requerido para acceder al detalle del lote");
    }

    const url = `${BASE}/${lotId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: createAuthHeaders(token),
      });

      // 404 = el lote no existe (no debería ocurrir desde el catálogo)
      if (response.status === 404) {
        return [];
      }

      const data = await handleResponse(response);
      return Array.isArray(data?.data) ? data.data : [];
    } catch (error) {
      console.error(`❌ Error fetching lot details for lot ${lotId}:`, error);
      throw error;
    }
  },

  /**
   * Asignar la sección de cotización de una o más líneas de detalle.
   * Solo se envían las líneas que cambian; cada una con { id, quoteSection }.
   * quoteSection acepta 'characteristics', 'documentation' o null (sin asignar).
   *
   * @param {number|string} lotId
   * @param {Array<{id:number, quoteSection:(string|null)}>} sections
   * @param {string} token
   * @returns {Promise<Array>} las líneas actualizadas
   */
  updateLotDetailSections: async (lotId, sections, token) => {
    if (!token) {
      throw new Error("Token requerido para guardar el detalle del lote");
    }

    const url = `${BASE}/${lotId}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: createAuthHeaders(token),
        body: JSON.stringify({ sections }),
      });

      const data = await handleResponse(response);
      return Array.isArray(data?.data) ? data.data : [];
    } catch (error) {
      console.error(`❌ Error saving lot details for lot ${lotId}:`, error);
      throw error;
    }
  },
};

export default lotDetailsService;
